// C2-S — pure geometry + spatial index for the latitude×|dR| scatter.
//
// This is the single source of truth for the point projection that the (former)
// SVG used, now shared by the canvas renderer and its unit tests. All pixel
// coordinates live in the fixed 700×360 viewBox space; the canvas scales this
// space uniformly to its DPR-aware backing store, so the rendering stays
// pixel-faithful to the SVG at any device size.

export const WIDTH = 700;
export const HEIGHT = 360;
export const PAD = { left: 50, right: 16, top: 16, bottom: 40 } as const;
export const POINT_RADIUS = 3.5;
export const POINT_OPACITY = 0.7;

/** Per-GNSS-config colours — byte-identical to the SVG's SYSTEM_COLORS. Only
 *  these five combos have a dedicated colour; every other config combo (mixed
 *  dual/triple systems) falls back to the theme's `--ink-3`, exactly as the SVG
 *  did via `fill="var(--ink-3)"`. */
export const SYSTEM_COLORS: Record<string, string> = {
  gps: '#2563eb',
  glonass: '#16a34a',
  galileo: '#ea580c',
  beidou: '#dc2626',
  'beidou,galileo,glonass,gps': '#7c3aed',
};
export const LEGEND_KEYS: string[] = Object.keys(SYSTEM_COLORS);

/** Default for the `--ink-3` fallback (light theme value from tokens.css). The
 *  canvas resolves the LIVE `--ink-3` at draw time and passes it in, so the
 *  on-screen colour matches the active theme; this constant only backs
 *  non-DOM callers (tests / SSR). */
export const FALLBACK_COLOR = '#7c7965';

export function configColor(config: string, fallback: string = FALLBACK_COLOR): string {
  return SYSTEM_COLORS[config] ?? fallback;
}

const yLog = (dr: number): number => Math.log10(Math.max(dr, 0.1));

export function computeYBounds(points: ReadonlyArray<{ dr_3d_mm: number }>): { yMin: number; yMax: number } {
  if (points.length === 0) return { yMin: 0, yMax: 1 };
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of points) {
    const v = yLog(p.dr_3d_mm);
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (lo === hi) {
    lo -= 1;
    hi += 1;
  }
  return { yMin: lo, yMax: hi };
}

export function projectX(lat: number): number {
  return PAD.left + ((lat + 90) / 180) * (WIDTH - PAD.left - PAD.right);
}

export function projectY(dr: number, yMin: number, yMax: number): number {
  return HEIGHT - PAD.bottom - ((yLog(dr) - yMin) / (yMax - yMin)) * (HEIGHT - PAD.top - PAD.bottom);
}

// ── spatial grid (viewBox-space nearest hit-test) ──────────────────────────
export interface Grid {
  cell: number;
  cols: number;
  buckets: Map<number, number[]>;
  pts: ReadonlyArray<{ x: number; y: number }>;
}

const keyOf = (cx: number, cy: number, cols: number): number => cy * cols + cx;

/** Bucket the projected points into a uniform grid so pointer hit-testing scans
 *  only the local neighbourhood, never all 14587 points. */
export function buildGrid(pts: ReadonlyArray<{ x: number; y: number }>, cell = 16): Grid {
  const cols = Math.ceil(WIDTH / cell) + 2;
  const buckets = new Map<number, number[]>();
  for (let i = 0; i < pts.length; i++) {
    const cx = Math.floor(pts[i]!.x / cell);
    const cy = Math.floor(pts[i]!.y / cell);
    const k = keyOf(cx, cy, cols);
    const b = buckets.get(k);
    if (b) b.push(i);
    else buckets.set(k, [i]);
  }
  return { cell, cols, buckets, pts };
}

/** Nearest point index within `maxDist` (viewBox units), or null. On ties the
 *  later-drawn (visually top) point wins — matches the SVG paint order. */
export function nearest(grid: Grid, x: number, y: number, maxDist: number): number | null {
  const { cell, cols, buckets, pts } = grid;
  const cx = Math.floor(x / cell);
  const cy = Math.floor(y / cell);
  const r = Math.ceil(maxDist / cell);
  let best = -1;
  let bestD = maxDist * maxDist;
  for (let gy = cy - r; gy <= cy + r; gy++) {
    for (let gx = cx - r; gx <= cx + r; gx++) {
      const b = buckets.get(keyOf(gx, gy, cols));
      if (!b) continue;
      for (const i of b) {
        const dx = pts[i]!.x - x;
        const dy = pts[i]!.y - y;
        const d = dx * dx + dy * dy;
        if (d < bestD || (d === bestD && i > best)) {
          bestD = d;
          best = i;
        }
      }
    }
  }
  return best >= 0 ? best : null;
}
